/**
 * A building's Contacts: the main phone and the people the workforce calls,
 * grouped by role, kept in eMerge (D-30; authority ruling 2026-10-05: facility
 * contacts are EMERGE_OWNED, Monday is history only).
 *
 * Shown in the Scheduling facility panel (the board's row and the facility
 * table) and the Business Development account panel. Every line says where it came from. A Monday line eMerge has not
 * taken over is labelled as such and can be adopted, edited or ended here;
 * Monday never changes a line once eMerge holds it.
 *
 * Writes appear only when the server says this caller may make them
 * (`can.write`). A caller without facility.contact.write sees the panel's own
 * copy of the directory, read-only, and is told so. A failed read is said,
 * never shown as an empty list: with no copy to fall back on (or a copy the
 * panel itself could not read, `fallbackError`), a refused read is a failure.
 *
 * Focus: opening a form moves focus to its first field; closing it (Cancel,
 * or once the change is recorded) returns focus to the button that opened it,
 * or, when that line is gone, to its role group.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  facilityContactsApi, freshKey, refusalText, NO_ANSWER, EARLIER_SAVED, STALE_CODES, CONTACT_ROLE_LABEL, CARD_ROLES, PERSON_ROLES,
  ContactDirectory, ContactHistoryEntry, ContactLine, ContactRole, ContactMode, MainPhoneLine, ApiResult,
} from './facilityContactsApi';
import './facilityContacts.css';

const shortDay = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' });
};

/** Where a line came from, quietly, under it. */
export function Provenance({ line, canWrite = false }: { line: ContactLine; canWrite?: boolean }) {
  // What this viewer can do about it is said only to someone who can.
  if (line.source === 'MONDAY') {
    return <span className="fc-prov monday">From Monday, not adopted yet. Monday no longer changes it.{canWrite ? ' Adopt, edit or end it here.' : ''}</span>;
  }
  if (line.source === 'LEGACY') {
    // A number on a building with no Monday item: not Monday's, so never "From Monday" and never adoptable.
    return <span className="fc-prov">On the building record before eMerge kept contacts.{canWrite ? ' Correct it here to keep it in eMerge.' : ''}</span>;
  }
  const when = shortDay(line.effective_from);
  // The call-sheet view leaves out who recorded a line: say only that eMerge keeps it.
  const by = line.recorded_by_name ? ` by ${line.recorded_by_name}` : '';
  const head = line.origin_system === 'MONDAY'
    ? `Adopted from Monday${line.origin_item_id ? ` (item ${line.origin_item_id})` : ''} · kept in eMerge${by}`
    : `Recorded in eMerge${by}`;
  return <span className="fc-prov">{head}{when ? ` on ${when}` : ''}{line.reason ? ` · ${line.reason}` : ''}</span>;
}

type Act =
  | { kind: 'add'; role: ContactRole }
  | { kind: 'edit' | 'replace' | 'end'; line: ContactLine }
  | { kind: 'main' };

const EVENT_LABEL: Record<string, string> = {
  ADDED: 'Added', EDITED: 'Edited', REPLACED: 'Replaced', ENDED: 'Ended', ADOPTED: 'Adopted from Monday', MAIN_PHONE_SET: 'Main phone recorded', MAIN_PHONE_ENDED: 'Main phone ended',
};

export default function FacilityContactsSection({ facilityId, facilityName, fallback = null, fallbackError = false, fallbackPending = false }: {
  facilityId: string; facilityName: string;
  /** The directory the panel already holds, shown read-only when this caller may not keep contacts. */
  fallback?: ContactDirectory | null;
  /** The panel tried to read the directory and could not: never shown as "no contacts". */
  fallbackError?: boolean;
  /** The panel is still reading its copy: a refused read waits for it rather than calling itself a failure. */
  fallbackPending?: boolean;
}) {
  const headingId = useId();
  const sectionRef = useRef<HTMLElement | null>(null);
  /** Where focus goes when the open form closes: its trigger, else its role group, else the heading. */
  const returnTo = useRef<{ trigger: string; group: string } | null>(null);
  const [focusBack, setFocusBack] = useState(false);
  /** One Idempotency-Key per adopt act, kept until a definitive answer (success or a 4xx), so a retry after no answer or a 5xx is answered once. */
  const adoptKeys = useRef<Map<string, string>>(new Map());
  const adoptKey = (k: string): string => {
    const m = adoptKeys.current;
    if (!m.has(k)) m.set(k, freshKey());
    return m.get(k) as string;
  };
  // A key is spent only by a definitive answer: success, or a refusal (4xx). A 5xx can follow a committed
  // adoption (the directory is read after the commit), and no answer at all proves nothing: both keep the key,
  // so the retry replays the first request instead of making a second one. 408 and 429 are not refusals of it.
  const spends = (r: ApiResult<unknown>): boolean => r.success
    || (r.status !== undefined && r.status >= 400 && r.status < 500 && r.status !== 408 && r.status !== 429);
  const [dir, setDir] = useState<ContactDirectory | null>(null);
  const [history, setHistory] = useState<ContactHistoryEntry[]>([]);
  const [canWrite, setCanWrite] = useState(false);
  /** This caller may not read the contacts route (403/404). What that means is decided when shown: it depends on the panel's copy, which may arrive later. */
  const [refused, setRefused] = useState(false);
  const [readErr, setReadErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [act, setAct] = useState<Act | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [bulkErr, setBulkErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setReadErr(null);
    try {
      const r = await facilityContactsApi.read(facilityId);
      if (r.success && r.data) {
        setDir(r.data.directory); setHistory(r.data.history || []); setCanWrite(Boolean(r.data.can && r.data.can.write)); setRefused(false);
      } else if (r.status === 403 || r.status === 404) {
        setDir(null); setCanWrite(false); setRefused(true);
      } else {
        setCanWrite(false); setRefused(false); setReadErr(`Could not read this building’s contacts. ${refusalText(r, { read: true })}`);
      }
    } catch {
      setCanWrite(false); setRefused(false); setReadErr('Could not read this building’s contacts. Could not reach the server.');
    } finally { setLoading(false); }
  }, [facilityId]);
  // Re-read when the building changes; a new copy from the panel does not re-read.
  useEffect(() => { setAct(null); setNotice(null); load(); }, [facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!focusBack || act || loading) return;
    setFocusBack(false);
    const root = sectionRef.current;
    const target = returnTo.current;
    returnTo.current = null;
    if (!root) return;
    const pick = (sel: string) => root.querySelector<HTMLElement>(sel);
    const el = (target && (pick(`[data-fc-trigger="${target.trigger}"]`) || pick(`[data-fc-group="${target.group}"]`))) || pick('h3');
    if (el) el.focus();
  }, [focusBack, act, loading]);
  const close = () => { setAct(null); setFocusBack(true); };

  const shown: ContactDirectory | null = dir || fallback;
  // A refused read with the panel's copy in hand is read-only; with none (or one the panel could not read) it is a failure,
  // never an empty section; while the panel is still reading, it waits.
  const hasCopy = Boolean(fallback) && !fallbackError;
  const readOnlyNote = refused && hasCopy ? 'Read-only: keeping this building’s contacts is not open for this account.' : null;
  const waiting = refused && !hasCopy && !fallbackError && fallbackPending;
  const shownErr = readErr || (refused && !hasCopy && !waiting ? 'Could not read this building’s contacts.' : null);
  const done = (msg: string) => async (next?: ContactDirectory) => {
    setAct(null); setNotice(msg); setBulkErr(null);
    if (next) setDir(next);
    await load();
    setFocusBack(true);
  };
  const open = (a: Act, trigger: string, group: string) => { returnTo.current = { trigger, group }; setNotice(null); setBulkErr(null); setAct(a); };
  const isOpen = (kind: Act['kind'], id: string | null | undefined) => Boolean(act && act.kind === kind && ('line' in act ? act.line.id === id : 'role' in act ? act.role === id : id === 'main'));

  const adoptAll = async () => {
    setBusy(true); setBulkErr(null); setNotice(null);
    try {
      const r = await facilityContactsApi.adoptAll(facilityId, adoptKey('all'));
      if (spends(r)) adoptKeys.current.delete('all');
      if (!r.success || !r.data) { setBulkErr(refusalText(r)); return; }
      // A replay (the earlier request was recorded; its answer was lost or was a 5xx) adopts nothing new and carries
      // no count of what the first one adopted: say it was already recorded, never "0 adopted".
      if (r.data.replayed) { await done('That adoption was already recorded by your earlier try. The contacts below are current.')(r.data.directory); return; }
      const left = r.data.needs_edit.length;
      await done(`${r.data.adopted.length} Monday line${r.data.adopted.length === 1 ? '' : 's'} adopted into eMerge.${left ? ` ${left} need${left === 1 ? 's' : ''} an Edit first.` : ''}`)(r.data.directory);
    } catch { setBulkErr(NO_ANSWER); } finally { setBusy(false); }
  };
  const adoptOne = async (line: ContactLine | MainPhoneLine, label: string) => {
    if (!line.id) return;
    setBusy(true); setBulkErr(null); setNotice(null);
    try {
      const r = await facilityContactsApi.adopt(facilityId, line.id, adoptKey(line.id));
      if (spends(r)) adoptKeys.current.delete(line.id);
      if (!r.success || !r.data) { setBulkErr(refusalText(r)); if (STALE_CODES.has(String(r.code))) await load(); return; }
      await done(`${label} adopted into eMerge. Monday will not change it.`)(r.data.directory);
    } catch { setBulkErr(NO_ANSWER); } finally { setBusy(false); }
  };

  const main = shown ? shown.main_phone : null;
  const missing = new Set<ContactRole>(shown ? shown.missing_roles : []);
  const linesIn = (role: ContactRole): ContactLine[] => (shown ? shown.contacts.filter((c) => (c.role || 'OTHER') === role) : []);
  const adoptable = shown && typeof shown.adoptable === 'number' ? shown.adoptable : 0;

  return (
    <section className="fc-section" aria-labelledby={headingId} ref={sectionRef}>
      <div className="fc-head">
        <h3 id={headingId} tabIndex={-1}>Contacts</h3>
        <span className="fc-prov">Kept in eMerge. Monday is history only.</span>
      </div>
      {(loading || waiting) && !shown && <p className="um-quiet" role="status">Loading contacts…</p>}
      {shownErr && (
        <p className="um-notice stop" role="alert">
          {shownErr}{hasCopy ? ' Showing the copy this panel already has, read-only.' : ''} <button type="button" className="um-open" onClick={load}>Try again</button>
        </p>
      )}
      {readOnlyNote && <p className="um-quiet">{readOnlyNote}</p>}
      {notice && <p className="um-notice ok" role="status">{notice}</p>}
      {bulkErr && <p className="um-notice stop" role="alert">{bulkErr}</p>}

      {shown && canWrite && adoptable > 0 && (
        <div className="fc-adopt">
          <span>{adoptable} line{adoptable === 1 ? '' : 's'} from Monday {adoptable === 1 ? 'is' : 'are'} not adopted yet. Adopting makes {adoptable === 1 ? 'it an eMerge record' : 'them eMerge records'}, with Monday kept as provenance.</span>
          <button type="button" className="um-open" disabled={busy} onClick={adoptAll}>{busy ? 'Adopting…' : 'Adopt all from Monday'}</button>
        </div>
      )}

      {shown && (
        <>
          <div className="fc-role" role="group" aria-label="Main phone" data-fc-group="MAIN" tabIndex={-1}>
            <h4>Main phone</h4>
            {main && main.phone ? (
              <div className="fc-card">
                <span className="how"><a href={`tel:${main.phone}`}>{main.phone}</a></span>
                <Provenance line={main} canWrite={canWrite} />
                {main.monday_phone && <span className="fc-prov">Monday’s old number (history only): {main.monday_phone}</span>}
              </div>
            ) : <span className="fc-none">Not recorded</span>}
            {canWrite && !isOpen('main', 'main') && !(main && isOpen('end', main.id)) && (
              <div className="fc-acts">
                <button type="button" className="um-open" data-fc-trigger="main" onClick={() => open({ kind: 'main' }, 'main', 'MAIN')}
                  aria-label={`${main && main.phone ? 'Correct the number' : 'Record the number'}: main number for ${facilityName}`}>{main && main.phone ? 'Correct the number' : 'Record the number'}</button>
                {main && main.adoptable && main.id && <button type="button" className="um-open" disabled={busy} onClick={() => adoptOne(main, 'The main number')} aria-label={`Adopt the main number for ${facilityName} from Monday`}>Adopt</button>}
                {/* Only eMerge's own number is ended here; Monday's is adopted or corrected, never ended. Ending leaves "not recorded". */}
                {main && main.source === 'EMERGE' && main.id && main.phone && (
                  <button type="button" className="um-open" data-fc-trigger="end:main" onClick={() => open({ kind: 'end', line: main }, 'end:main', 'MAIN')}
                    aria-label={`End: main number for ${facilityName}`}>End</button>
                )}
              </div>
            )}
            {canWrite && main && main.id && isOpen('end', main.id) && (
              <EndForm facilityId={facilityId} contactId={main.id} who={`the main number for ${facilityName}`} question="Why is this no longer the building’s number?" onCancel={close} onStale={load}
                onDone={done('Main number ended. The building shows no main number until one is recorded.')} />
            )}
            {canWrite && isOpen('main', 'main') && (
              <ContactForm facilityId={facilityId} role="MAIN" mode="REPLACE" replaces={main && main.source === 'EMERGE' ? main.id : (main ? main.id : null)}
                initial={{ phone: main?.phone || '' }} label={`Correct the main number for ${facilityName}`}
                onCancel={close} onStale={load} onDone={done('Main number recorded. Everyone sees it now.')} />
            )}
          </div>

          <div className="fc-grid">
            {CARD_ROLES.map((role) => {
              const lines = linesIn(role);
              const label = CONTACT_ROLE_LABEL[role];
              return (
                <div key={role} className={`fc-role${missing.has(role) ? ' missing' : ''}`} role="group" aria-label={label} data-fc-group={role} tabIndex={-1}>
                  <h4>{label}</h4>
                  {lines.length === 0 && <span className="fc-none">{PERSON_ROLES.includes(role) ? 'Not recorded' : 'None recorded'}</span>}
                  {lines.map((c, i) => (
                    <ContactCard key={c.id || `${role}-${i}`} facilityId={facilityId} line={c} canWrite={canWrite} busy={busy} act={act}
                      onOpen={(a, trigger) => open(a, trigger, role)} onCancel={close} onStale={load} onDone={done} onAdopt={() => adoptOne(c, c.name || label)} />
                  ))}
                  {canWrite && (lines.length === 0 || role === 'OTHER') && !isOpen('add', role) && (
                    <div className="fc-acts">
                      <button type="button" className="um-open" data-fc-trigger={`add:${role}`} onClick={() => open({ kind: 'add', role }, `add:${role}`, role)}
                        aria-label={`${role === 'OTHER' && lines.length > 0 ? 'Add another' : 'Add'}: ${label}`}>{role === 'OTHER' && lines.length > 0 ? 'Add another' : 'Add'}</button>
                    </div>
                  )}
                  {canWrite && isOpen('add', role) && (
                    <ContactForm facilityId={facilityId} role={role} mode="ADD" initial={{}} label={`Add a contact: ${label}`}
                      onCancel={close} onStale={load} onDone={done(`${label} recorded.`)} />
                  )}
                </div>
              );
            })}
          </div>

          {history.length > 0 && (
            <details className="fc-history">
              <summary>Recent changes ({history.length})</summary>
              <ul>
                {history.map((h) => {
                  const who = (h.after && (h.after.name || h.after.phone)) || (h.before && (h.before.name || h.before.phone)) || null;
                  return (
                    <li key={h.id}>
                      {shortDay(h.recorded_at) || 'date not recorded'} · {EVENT_LABEL[h.event_type] || h.event_type}{h.role ? ` · ${CONTACT_ROLE_LABEL[h.role]}` : ''}{who ? ` · ${who}` : ''} · {h.actor_name || 'someone in eMerge'} · {h.reason}
                    </li>
                  );
                })}
              </ul>
            </details>
          )}
        </>
      )}
      {!shown && !loading && !waiting && !shownErr && !readOnlyNote && <p className="fc-none">Contacts not recorded.</p>}
    </section>
  );
}

function ContactCard({ facilityId, line, canWrite, busy, act, onOpen, onCancel, onStale, onDone, onAdopt }: {
  facilityId: string; line: ContactLine; canWrite: boolean; busy: boolean; act: Act | null;
  onOpen: (a: Act, trigger: string) => void; onCancel: () => void; onStale: () => void;
  onDone: (msg: string) => (next?: ContactDirectory) => Promise<void>; onAdopt: () => void;
}) {
  const role: ContactRole = line.role || 'OTHER';
  const label = CONTACT_ROLE_LABEL[role];
  const who = line.name || 'Name not recorded';
  const openKind = act && 'line' in act && act.line.id === line.id ? act.kind : null;
  return (
    <article className="fc-card" aria-label={`${label}: ${who}`}>
      <span className="who">{who}{line.title && line.title !== label ? <span className="fc-prov" style={{ display: 'inline', marginLeft: 6 }}>{line.title}</span> : null}</span>
      <span className="how">
        {line.phone ? <a href={`tel:${line.phone}`}>{line.phone}</a> : <span className="fc-none">phone not recorded</span>}
        {' · '}
        {line.email ? <a href={`mailto:${line.email}`}>{line.email}</a> : <span className="fc-none">email not recorded</span>}
      </span>
      {line.notes && <span className="notes">{line.notes}</span>}
      <Provenance line={line} canWrite={canWrite} />
      {canWrite && line.id && !openKind && (
        <div className="fc-acts">
          {line.adoptable && <button type="button" className="um-open" disabled={busy} onClick={onAdopt} aria-label={`Adopt ${label} ${who} from Monday`}>Adopt</button>}
          <button type="button" className="um-open" data-fc-trigger={`edit:${line.id}`} onClick={() => onOpen({ kind: 'edit', line }, `edit:${line.id}`)} aria-label={`Edit ${label} ${who}`}>Edit</button>
          <button type="button" className="um-open" data-fc-trigger={`replace:${line.id}`} onClick={() => onOpen({ kind: 'replace', line }, `replace:${line.id}`)} aria-label={`Replace ${label} ${who} with someone else`}>Replace</button>
          <button type="button" className="um-open" data-fc-trigger={`end:${line.id}`} onClick={() => onOpen({ kind: 'end', line }, `end:${line.id}`)} aria-label={`End ${label} ${who}`}>End</button>
        </div>
      )}
      {canWrite && line.id && (openKind === 'edit' || openKind === 'replace') && (
        <ContactForm facilityId={facilityId} role={role} mode={openKind === 'edit' ? 'EDIT' : 'REPLACE'} replaces={line.id}
          initial={openKind === 'edit' ? { name: line.name || '', title: line.title || '', phone: line.phone || '', email: line.email || '', notes: line.notes || '' } : {}}
          label={openKind === 'edit' ? `Edit ${label} ${who}` : `Replace ${label} ${who}`} allowRole={openKind === 'edit'}
          onCancel={onCancel} onStale={onStale}
          onDone={onDone(openKind === 'edit' ? `${label} updated. The earlier entry is kept in the history.` : `${label} replaced. The earlier entry is kept in the history.`)} />
      )}
      {canWrite && line.id && openKind === 'end' && (
        <EndForm facilityId={facilityId} contactId={line.id} who={line.name || label} onCancel={onCancel} onStale={onStale}
          onDone={onDone(`${line.name || label} is no longer listed. The entry is kept in the history.`)} />
      )}
    </article>
  );
}

/** Add, edit, replace, or correct the main number. Every change says why, or how the person learned of it. */
function ContactForm({ facilityId, role: initialRole, mode, replaces = null, initial, label, allowRole = false, onCancel, onDone, onStale }: {
  facilityId: string; role: ContactRole; mode: ContactMode; replaces?: string | null; label: string;
  initial: { name?: string; title?: string; phone?: string; email?: string; notes?: string };
  /** An edit may correct a misread role (into a role nobody holds; the server says if it is taken). */
  allowRole?: boolean;
  onCancel: () => void; onDone: (next?: ContactDirectory) => Promise<void>; onStale: () => void;
}) {
  const uid = useId();
  const [role, setRole] = useState<ContactRole>(initialRole);
  const [name, setName] = useState(initial.name || '');
  const [title, setTitle] = useState(initial.title || '');
  const [phone, setPhone] = useState(initial.phone || '');
  const [email, setEmail] = useState(initial.email || '');
  const [notes, setNotes] = useState(initial.notes || '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [key, setKey] = useState(freshKey);
  const formRef = useRef<HTMLFormElement | null>(null);
  useEffect(() => { formRef.current?.querySelector<HTMLElement>('select, input, textarea')?.focus(); }, []);
  const isMain = role === 'MAIN';
  const ready = reason.trim().length >= 3 && (isMain ? phone.trim() !== '' : name.trim() !== '');
  const id = (f: string) => `${uid}-${f}`;
  return (
    <form className="fc-form um-form" aria-label={label} ref={formRef} onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        const r: ApiResult<{ directory: ContactDirectory }> = await facilityContactsApi.record(facilityId, {
          role, mode: isMain ? undefined : mode, name: isMain ? null : name.trim(), title: isMain ? null : (title.trim() || null), phone: phone.trim() || null,
          email: isMain ? null : (email.trim() || null), notes: isMain ? null : (notes.trim() || null), reason: reason.trim(), replaces_contact_id: replaces,
        }, key);
        // This form's key already used: an earlier try was recorded. Say so, reload, and take a fresh key (never stuck on KEY_REUSED).
        if (!r.success && r.code === 'KEY_REUSED') { setKey(freshKey()); setErr(EARLIER_SAVED); onStale(); return; }
        if (!r.success || !r.data) { setErr(refusalText(r)); if (STALE_CODES.has(String(r.code))) onStale(); return; }
        await onDone(r.data.directory);
      } catch { setErr(NO_ANSWER); } finally { setBusy(false); }
    }}>
      {allowRole && (
        <label htmlFor={id('role')}><span>Role</span>
          <select id={id('role')} value={role} onChange={(e) => setRole(e.target.value as ContactRole)}>
            {CARD_ROLES.map((r) => <option key={r} value={r}>{CONTACT_ROLE_LABEL[r]}</option>)}
          </select>
        </label>
      )}
      {!isMain && <label htmlFor={id('name')}><span>Name</span><input id={id('name')} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required autoComplete="off" /></label>}
      {!isMain && <label htmlFor={id('title')}><span>Title (optional)</span><input id={id('title')} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder={role === 'OTHER' ? 'e.g. Receptionist' : CONTACT_ROLE_LABEL[role]} /></label>}
      <label htmlFor={id('phone')}><span>{isMain ? 'Main number' : 'Phone (optional)'}</span><input id={id('phone')} value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} inputMode="tel" placeholder="813-555-0100" required={isMain} /></label>
      {!isMain && <label htmlFor={id('email')}><span>Email (optional)</span><input id={id('email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} /></label>}
      {!isMain && <label htmlFor={id('notes')}><span>Notes (optional)</span><textarea id={id('notes')} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} /></label>}
      <label htmlFor={id('reason')}><span>Why, or how you learned of it</span><input id={id('reason')} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} required aria-describedby={id('reason-help')} placeholder="e.g. The front desk told me on 10/2" /></label>
      <span id={id('reason-help')} className="fc-hint">{isMain ? 'A number and' : 'A name and'} a reason of at least 3 characters are needed to record.</span>
      <div className="btns">
        <button type="submit" className="um-open um-primary" disabled={busy || !ready}>{busy ? 'Recording…' : 'Record'}</button>
        <button type="button" className="um-open" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </form>
  );
}

/** "No longer there." The entry stays in the history; the reason is required. */
function EndForm({ facilityId, contactId, who, question = null, onCancel, onDone, onStale }: {
  /** The visible question; by default "Why is <who> no longer the contact?". */
  question?: string | null;
  facilityId: string; contactId: string; who: string; onCancel: () => void; onDone: (next?: ContactDirectory) => Promise<void>; onStale: () => void;
}) {
  const uid = useId();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [key, setKey] = useState(freshKey);
  const reasonRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { reasonRef.current?.focus(); }, []);
  return (
    <form className="fc-form um-form" aria-label={question ? `End ${who}` : `${who} is no longer the contact`} onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        const r = await facilityContactsApi.end(facilityId, contactId, reason.trim(), key);
        // This form's key already used: an earlier try was recorded. Say so, reload, and take a fresh key (never stuck on KEY_REUSED).
        if (!r.success && r.code === 'KEY_REUSED') { setKey(freshKey()); setErr(EARLIER_SAVED); onStale(); return; }
        if (!r.success || !r.data) { setErr(refusalText(r)); if (STALE_CODES.has(String(r.code))) onStale(); return; }
        await onDone(r.data.directory);
      } catch { setErr(NO_ANSWER); } finally { setBusy(false); }
    }}>
      <label htmlFor={`${uid}-why`}><span>{question || `Why is ${who} no longer the contact?`}</span><input id={`${uid}-why`} ref={reasonRef} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} required aria-describedby={`${uid}-why-help`} placeholder="e.g. Left the building in September" /></label>
      <span id={`${uid}-why-help`} className="fc-hint">A reason of at least 3 characters is needed to record.</span>
      <div className="btns">
        <button type="submit" className="um-open um-primary" disabled={busy || reason.trim().length < 3}>{busy ? 'Recording…' : 'Confirm: End'}</button>
        <button type="button" className="um-open" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </form>
  );
}
